import { useEffect, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

export function ShellDialog({ open, onClose, title, children, className = '', id, modal = true }: {
  open: boolean; onClose: () => void; title: string; children: ReactNode; className?: string; id: string; modal?: boolean
}) {
  const { t } = useTranslation('common')
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const dialog = ref.current
    if (!dialog || !open) return
    const trigger = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    const rootOverflow = document.documentElement.style.overflow
    const rootScrollbarGutter = document.documentElement.style.scrollbarGutter
    if (modal) {
      dialog.showModal()
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      document.documentElement.style.scrollbarGutter = 'auto'
    } else dialog.show()
    const focusable = () => [...dialog.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]')].filter((element) => element.getClientRects().length)
    focusable()[0]?.focus({ preventScroll: true })
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return }
      if (!modal || event.key !== 'Tab') return
      const nodes = focusable()
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    dialog.addEventListener('keydown', keyboard)
    return () => {
      dialog.removeEventListener('keydown', keyboard)
      dialog.close()
      if (modal) {
        document.body.style.overflow = overflow
        document.documentElement.style.overflow = rootOverflow
        document.documentElement.style.scrollbarGutter = rootScrollbarGutter
      }
      if (trigger?.isConnected) trigger.focus({ preventScroll: true })
    }
  }, [modal, onClose, open])
  return <dialog ref={ref} id={id} className={`shell-dialog ${className}`} aria-labelledby={`${id}-title`}
    onCancel={(event) => { event.preventDefault(); onClose() }}
    onClick={(event) => {
      if (event.target !== event.currentTarget) return
      const rect = event.currentTarget.getBoundingClientRect()
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose()
    }}>
    <div className="dialog-heading"><h2 id={`${id}-title`}>{title}</h2><button className="icon-button" onClick={onClose} aria-label={t('aria.close')}><X aria-hidden="true" /></button></div>
    <div className="dialog-content">{children}</div>
  </dialog>
}
